import React, { useState } from "react";
import { Modal, Button } from "react_reusable_components";
import variables from "../../themes/base/styles/variables";
import { encrypt, getLocalizedMessage } from "../../UtilityFunctions/utils";
import { useIntl } from "react-intl";
import { ThreeRow } from "../../core/layouts";
import { useFormik } from "formik";
import useResponsive from "core/hooks/useResponsive";
import ResetPasswordModalHeader from "../ResetPasswordModalHeader";
import {
  changePasswordFieldNames,
  changePasswordValidationSchema,
  changePasswordInitialValues,
} from "../../validationSchema/changePasswordSchema";
import SetPassword from "../../components/SetPassword/SetPassword";
import MyAccountServices from "../../services/my-account-services";
import { useParams } from "react-router-dom";

const ChangePasswordStyle = {
  modalContent: { width: variables.fullWidth },
  modalMaskStyle: {
    backdropFilter: variables.highBlur,
    background: variables.blurBackground,
  },
  topSectionStyle: { borderBottom: `1px solid ${variables.grey4}` },
  bottomSectionStyle: {
    borderTop: `1px solid ${variables.grey4}`,
    display: variables.dFlex,
    justifyContent: "end",
    padding: variables.paddingMedium,
    gap: variables.gapMediumSmall,
  },
  middleSectionStyle: { padding: `${variables.paddingMedium}` },
  cancelBtn: {
    border: `1px solid ${variables.grey4}`,
    backgroundColor: variables.bodyBg,
    color: variables.textDefault,
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
    lineHeight: variables.lineHeightNormal,
    height: variables.buttonHeightNormal,
  },
  changeBtn: {
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
    lineHeight: variables.lineHeightNormal,
    height: variables.buttonHeightNormal,
  },
};

export default function ChangePasswordModal({ open, onClose }) {
  const intl = useIntl();
  const [disabled, setDisabled] = useState(false);
  const { id } = useParams();

  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
    resetForm,
  } = useFormik({
    initialValues: changePasswordInitialValues,
    onSubmit: async function (data) {
      setDisabled(true);
      try {
        MyAccountServices.changeUserPassword({
          body: {
            userId:Number(id),
            newPassword: encrypt(data[changePasswordFieldNames.PASSWORD]),
          },
          intl,
        });
        onClose();
        setDisabled(false);
      } catch (error) {
        setDisabled(false);
      }
    },
    validationSchema: changePasswordValidationSchema(intl),
  });

  const closeModal = () => {
    onClose();
    resetForm();
  };

  const responsive = useResponsive();

  return (
    <Modal
      maskClosable={false}
      maskStyle={ChangePasswordStyle.modalMaskStyle}
      centered
      open={open}
      closable={false}
      footer={null}
      width={549}
      disableGutter
    >
      <ThreeRow
        topSection={<ResetPasswordModalHeader onClose={closeModal} />}
        topSectionStyle={ChangePasswordStyle.topSectionStyle}
        middleSectionStyle={ChangePasswordStyle.middleSectionStyle}
        middleSection={
          <SetPassword
            size={"small"}
            values={values}
            fieldNames={changePasswordFieldNames}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
            labels={{
              [changePasswordFieldNames.PASSWORD]: getLocalizedMessage(
                intl,
                "login.label.newPassword"
              ),
              [changePasswordFieldNames.CONFIRM_PASSWORD]: getLocalizedMessage(
                intl,
                "login.label.confirmPassword"
              ),
            }}
            placeholders={{
              [changePasswordFieldNames.PASSWORD]: getLocalizedMessage(
                intl,
                "login.placeholder.newPassword"
              ),
              [changePasswordFieldNames.CONFIRM_PASSWORD]: getLocalizedMessage(
                intl,
                "login.placeholder.confirmPassword"
              ),
            }}
            touched={touched}
          />
        }
        bottomSectionStyle={{
          ...ChangePasswordStyle.bottomSectionStyle,
          ...(!responsive.isMd && { justifyContent: "center" }),
        }}
        bottomSection={
          <>
            <Button
              onClick={closeModal}
              icon={null}
              style={ChangePasswordStyle.cancelBtn}
            >
              {getLocalizedMessage(intl, "button.text.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              style={ChangePasswordStyle.changeBtn}
              icon={null}
              disabled={disabled}
            >
              {getLocalizedMessage(intl, "button.text.save")}
            </Button>
          </>
        }
      />
    </Modal>
  );
}
